"use client";
import ButtonWithLoading from "@/components/ui/ButtonWithLoading";
import CreatePresetHero from "@/components/ui/Hero/CreatePresetHero";
import CoinSVG from "@/components/ui/SVGIcons/CoinSVG";
import SimpleHeader from "@/components/ui/Sections/SimpleHeader";
import TaskUtils from "@/components/ui/Tasks/TaskUtils";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { updateCurrentUserCachedData } from "@/hooks/QueryHooks/ReactQueryCachedDataManager";
import { useUpdateUserUsernameMutation } from "@/hooks/QueryHooks/ReactQueryWrapperMutationHooks";
import {
  useFetchCurrentUserQuery,
  useGetTasksDataQuery,
} from "@/hooks/QueryHooks/ReactQueryWrapperhooks";
import {
  LocalModelInputPresetEntryInput,
  ModelInputEntry,
  ModelInputOutputEntryType,
} from "@/modeltypings";
import { IBackendManagerInterface } from "@/utils/BackendManager";
import { localUserHasPlan } from "@/utils/DataModels/LocalUserutils";
import {
  getCreatePresetPath,
  subscriptionPricingPath,
} from "@/utils/NavigationUtils";
import { toastError, toastInfo, toastPromise } from "@/utils/ToastsManager";
import { PresetErrorCodes } from "@mltask/core/ErrorHandling/PresetErrors";
import {
  UserErrorCodes,
  userErrorMessageForCode,
} from "@mltask/core/ErrorHandling/UserErrors";
import { calculateTotalCreditAwardPerUsage } from "@mltask/core/Utils/QuotaHelper";
import {
  minLengthRegex,
  wholeNumbersRegex,
} from "@mltask/core/Utils/RegexHelper";
import { useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AiFillWarning } from "react-icons/ai";
import { MdOutlineDescription } from "react-icons/md";
import CreateUsername from "./CreateUsername";
import {
  ChunkType,
  InputChunk,
  PresetInputChunk,
  UserInputChunk,
} from "./InputPresetChunkUI";

const TaskCardHeader = dynamic(
  () => import("@/components/ui/Tasks/TaskCard/TaskCardHeader"),
  {
    ssr: false,
  }
);
const SharePreset = dynamic(() => import("./SharePreset"), {
  ssr: false,
});
const InputPresetUI = dynamic(() => import("./InputPresetUI"), {
  ssr: false,
});
var BackendManager: IBackendManagerInterface;
import("@/utils/BackendManager").then((lib) => {
  BackendManager = lib.default;
});

const SimpleTextInput = dynamic(
  () => import("@/components/ui/Input/SimpleTextInput"),
  {
    ssr: false,
  }
);
const BaseModalContainer = dynamic(
  () => import("@/components/ui/Feedback/BaseModalContainer"),
  {
    ssr: false,
  }
);

type Props = { targetModelID?: string };

export default function CreateModelPresetUI({ targetModelID }: Props) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useFetchCurrentUserQuery();
  const { data: taskData, isError: getTaskError } = useGetTasksDataQuery();
  // console.log(taskData);

  const methods = useForm();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;
  const router = useRouter();
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTaskID, setSelectedTaskID] = useState<string | undefined>();
  const [selectedModelID, setSelectedModelID] = useState<string | undefined>(
    targetModelID
  );
  useEffect(() => {
    if (targetModelID != undefined && taskData != undefined) {
      let targetTaskID = TaskUtils.getTaskIDFromModelID(
        taskData,
        targetModelID
      );
      setSelectedTaskID(targetTaskID);
      router.replace(getCreatePresetPath());
    }
  }, [taskData]);

  const [presetName, setPresetName] = useState("");
  const [doesUserAgree, setDoesUserAgree] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);

  const [showingSubscriptionButton, setShowingSubscriptionButton] =
    useState(false);

  const [isPublic, setIsPublic] = useState(true);
  const [isOpenSource, setIsOpenSource] = useState(true);
  const [cost, setCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showingShare, setShowingShare] = useState(false);
  const loadingStyle = "loading-md loading loading-dots text-secondary";

  const [presetDescription, setPresetDescription] = useState("");

  const [isInputCommandOpen, setIsInputCommandOpen] = useState(false);
  const [entries, setEntries] = useState<LocalModelInputPresetEntryInput[]>([]);

  const [createdTaskID, setCreatedTaskID] = useState("");
  const [createdModelID, setCreatedModelID] = useState("");
  const [createdPresetID, setCreatedPresetID] = useState("");

  const updateUsernameMutation = useUpdateUserUsernameMutation(
    (variables) => {},
    (result, variables, context) => {
      setUsernameModalOpen(false);
      setConsentModalOpen(true);
      updateCurrentUserCachedData(
        {
          ...currentUser!,
          username: variables.username,
        },
        queryClient
      );
    },
    (error, variables, context) => {}
  );

  function mergeUpdate(updates: LocalModelInputPresetEntryInput) {
    var preExisting = entries.filter(
      (x) => x.modelInputName == updates.modelInputName
    );
    if (preExisting == null || preExisting.length == 0) {
      entries.push(updates);
    } else {
      const index = entries.indexOf(preExisting[0]);
      entries[index] = updates;
    }
    setEntries([...entries]);
  }
  function removeInputEntry(modelInputName: string) {
    const updatedEntries = [
      ...entries.filter((x) => x.modelInputName != modelInputName),
    ];
    setEntries(updatedEntries);
  }

  function renderInputSection(inputs: ModelInputEntry[]) {
    return (
      <div>
        {inputs.map((input, index) => {
          const ins = entries.find((entry) => {
            return entry.modelInputName == input.backendName;
          });
          var chunks: InputChunk[] | undefined = undefined;
          if (ins) {
            chunks = ins.presetEntryDatas.map((presetEntryData) => {
              if (presetEntryData.entryType == "PRESET_INPUT") {
                return {
                  type: ChunkType.PRESET_TEXT,
                  text: presetEntryData.val,
                } as PresetInputChunk;
              }
              if (presetEntryData.entryType == "USER_INPUT") {
                return {
                  type: ChunkType.USER_INPUT,
                  displayName: presetEntryData.parameterName,
                  description: presetEntryData.description,
                } as UserInputChunk;
              }
              return {} as InputChunk;
            });
          }
          if (chunks == undefined) {
            chunks = [
              {
                type: ChunkType.PRESET_TEXT,
                text: input.defaultValue ?? "",
              } as PresetInputChunk,
            ];
          }

          return (
            <div key={index} className="my-5 flex flex-row space-x-2 space-y-4">
              <InputPresetUI
                inputEntry={input as ModelInputEntry}
                items={chunks}
                onChunkUpdate={(updates) => {
                  mergeUpdate(updates);
                }}
                onChunkDelete={(chunkToDelete) => {
                  removeInputEntry(chunkToDelete.modelInputName!);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }
  function renderInputsSection() {
    if (taskData == undefined) return;
    if (selectedTaskID == undefined || selectedModelID == undefined) return;
    const modelInputs = taskData.entries
      .find((taskData) => taskData.id == selectedTaskID)
      ?.modelsInfo.find((model) => model.id == selectedModelID)?.inputs;
    if (modelInputs == undefined) return;
    const filteredMiniumRequired = modelInputs.entries
      .filter((x) => x.isRequired)
      .filter((x) => {
        const found = entries.find((y) => {
          return y.modelInputName == x.backendName;
        });
        return found != undefined;
      });

    const filteredAdvanced = modelInputs.entries
      .filter((x) => !x.isRequired)
      .filter((x) => {
        const found = entries.find((y) => {
          return y.modelInputName == x.backendName;
        });
        return found != undefined;
      });

    return (
      <div>
        {(filteredMiniumRequired.length > 0 || filteredAdvanced.length > 0) && (
          <div className="divider"></div>
        )}
        {renderInputSection(filteredMiniumRequired)}
        <div className="divider">{`${
          filteredAdvanced.length > 0 ? "Advanced Inputs" : ""
        }`}</div>
        {renderInputSection(filteredAdvanced)}
      </div>
    );
  }
  const [
    animateSubscriptionRequirementButton,
    setAnimateSubscriptionRequirementButton,
  ] = useState(false);
  const handleAnimation = () => {
    setAnimateSubscriptionRequirementButton(true);
    setTimeout(() => {
      setAnimateSubscriptionRequirementButton(false);
    }, 1200); // 1500ms is the duration of the animation
  };
  function renderShowingSubscriptionButton() {
    if (showingSubscriptionButton == false) return;
    return (
      <button
        type="button"
        className={`${
          animateSubscriptionRequirementButton ? "animate-wobble-scale" : ""
        } btn btn-accent`}
        onClick={() => {
          router.push(subscriptionPricingPath());
        }}
      >
        a Subscription is required to monetize your presets, select plan
      </button>
    );
  }
  function renderCostInputText() {
    if (currentUser == undefined) return;
    const userHasSubscription = localUserHasPlan(currentUser);
    const hintText = `you make $${calculateTotalCreditAwardPerUsage(
      cost
    )} / use`;
    return (
      <div className=" flex w-full flex-col space-y-2 overflow-hidden text-secondary-content">
        <SimpleTextInput
          onBlur={() => {
            if (userHasSubscription == false) {
              setCost(0);
              setShowingSubscriptionButton(true);
              handleAnimation();
            }
          }}
          titleTWClass="text-base-content font-bold"
          registrar={register("cost", {
            required: true,
            pattern: wholeNumbersRegex,
            onChange: (e) => {
              setCost(e.target.value);
            },
          })}
          errorSection={
            errors["cost"] && (
              <div className="mt-5 flex items-center rounded-md bg-secondary-content p-2">
                <span className="text-error">
                  Invalid cost, no decimals whole numbers only (i.e 0, 10, 100,
                  42 etc..)
                </span>
              </div>
            )
          }
          inputBadge={cost == 0 ? "FREE" : undefined}
          inputType="number"
          title="Cost"
          additionalTitleElement={
            cost == 0 ? undefined : (
              <div className="flex flex-col">
                {/* <span className="label-text font-bold text-accent ">
              you charge ${calculateCostPerUsage()} / use
            </span> */}
                <span className="label-text font-bold text-accent ">
                  {hintText}
                </span>
              </div>
            )
          }
          placeholder="Cost Per Inference"
          value={cost}
          SVGELement={<CoinSVG size={5} />}
        />
        {renderShowingSubscriptionButton()}
      </div>
    );
  }
  function renderPresetNameAndDescription() {
    return (
      <div className="flex w-full  flex-col space-y-2">
        <div className="flex w-full flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
          <div className="flex w-full flex-col text-secondary-content">
            <SimpleTextInput
              titleTWClass="text-base-content font-bold"
              registrar={register("presetName", {
                required: true,
                pattern: minLengthRegex(3),
                onChange: (e) => {
                  setPresetName(e.target.value);
                },
              })}
              errorSection={
                errors["presetName"] && (
                  <div className="mt-5 flex items-center rounded-md bg-secondary-content p-2">
                    <span className="text-error">
                      Invalid Name Length (Min 3)
                    </span>
                  </div>
                )
              }
              inputType="text"
              title="Name"
              placeholder="Preset name"
              value={presetName}
              pathD="M18 17h-7.5l2-2H18M6 17v-2.5l7.88-7.85c.19-.2.51-.2.71 0l1.76 1.76c.2.2.2.51 0 .71L8.47 17M19 3H5c-1.11 0-2 .89-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z"
            />
          </div>

          <div className="flex w-full flex-col text-secondary-content">
            <SimpleTextInput
              titleTWClass="text-base-content font-bold"
              registrar={register("presetDescription", {
                required: true,
                pattern: minLengthRegex(10),
                onChange: (e) => {
                  setPresetDescription(e.target.value);
                },
              })}
              errorSection={
                errors["presetDescription"] && (
                  <div className="mt-5 flex items-center rounded-md bg-secondary-content p-2">
                    <span className="text-error">
                      Invalid Description Length (Min 10)
                    </span>
                  </div>
                )
              }
              inputType="text"
              title="Description"
              placeholder="Preset Description"
              value={presetDescription}
              SVGELement={<MdOutlineDescription />}
            />
          </div>

          {renderCostInputText()}
        </div>
        <div className="divider"></div>
        <div className="flex w-full flex-col">
          <div className="form-control flex w-full flex-row space-x-2 font-bold">
            <input
              type="checkbox"
              checked={isOpenSource}
              onChange={(event) => {
                setIsOpenSource(event.target.checked);
              }}
              className="checkbox-accent checkbox my-auto"
            />
            <span className="label-text my-auto">is Open Source</span>
            <span className="rounded-md bg-primary p-1 text-primary-content">
              {isOpenSource
                ? "ANYONE can see how the preset is being constructed (recommended for Free presets)"
                : "NO one can see how the preset is constructed (recommended for monetization)"}
            </span>
          </div>
          <div className="divider"></div>
          <div className="form-control flex w-full flex-row space-x-2 font-bold">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(event) => {
                setIsPublic(event.target.checked);
              }}
              className="checkbox-accent checkbox my-auto"
            />
            <span className="label-text my-auto">is Public:</span>
            <span className="rounded-md bg-primary p-1 text-primary-content">
              {isPublic
                ? 'ANYONE can use the preset, NOTE: this does not let anyone see how the preset is made, that is determined by the "is Open Source" Switch above'
                : "only YOU can see and/or use that preset, no one else has access to it"}
            </span>
          </div>
        </div>
      </div>
    );
  }
  function renderTaskDrowDown() {
    if (taskData == undefined) return;
    return (
      <div
        className={`card mx-auto w-full border-none bg-base-content shadow-md shadow-neutral  `}
      >
        <TaskCardHeader
          tasks={taskData}
          taskID={selectedTaskID}
          modelID={selectedModelID}
          taskModalOpen={taskModalOpen}
          onTaskIDChange={setSelectedTaskID}
          onModelIDChange={setSelectedModelID}
          onTaskModalOpen={(isOpen) => {
            setTaskModalOpen(isOpen);
          }}
        />
      </div>
    );
  }
  function renderAddInputToPresetSection() {
    return (
      <div className="flex w-full">
        <button
          type="button"
          className={`btn btn-success w-full `}
          onClick={() => {
            setIsInputCommandOpen(!isInputCommandOpen);
          }}
        >
          Add Input
        </button>
        {addInputMenu()}
      </div>
    );
  }
  function renderModelInputMenuEntry(x: ModelInputEntry, index: number) {
    const enabled: boolean = true;
    // x.type == "TEXT" ||
    // x.type == "NUMBER" ||
    // x.type == "NUMBER_INT" ||
    // x.type == "NUMBER_FLOAT";
    return (
      <CommandItem
        disabled={!enabled}
        key={index}
        value={`${x.displayName}`}
        className={enabled ? "tooltip" : ""}
        data-tip={x.description}
        onSelect={(itemID) => {
          // setActivePreset(x);
          mergeUpdate({
            modelInputName: x.backendName,
            modelInputType: x.type,
            presetEntryDatas: [
              {
                entryType: "PRESET_INPUT",
                presetOrder: 0,
                val: x.defaultValue,
              },
            ],
          });
          // console.log("itemID = " + itemID);
          setIsInputCommandOpen(false);
        }}
      >
        <div>
          <span className={`${!enabled ? "text-gray-200" : ""}`}>
            {x.displayName} {!enabled ? " (Coming Soon)" : ""}
          </span>
        </div>
      </CommandItem>
    );
  }
  function addInputMenu() {
    if (taskData == undefined) return;

    const modelInputs = taskData.entries
      .find((taskData) => taskData.id == selectedTaskID)
      ?.modelsInfo.find((model) => model.id == selectedModelID)?.inputs;
    if (modelInputs == undefined) return;

    //filtering out inputs already added
    const adjustedMinimumRequired = modelInputs.entries
      .filter((x) => x.isRequired)
      .filter((minimumInput) => {
        const found = entries.find((entry) => {
          return entry.modelInputName == minimumInput.backendName;
        });
        return found == null;
      });

    //filtering out inputs already added
    const adjustedOptional = modelInputs.entries
      .filter((x) => !x.isRequired)
      .filter((x) => {
        const found = entries.find((y) => {
          return y.modelInputName == x.backendName;
        });
        return found == null;
      });
    return (
      <CommandDialog
        open={isInputCommandOpen}
        onOpenChange={setIsInputCommandOpen}
      >
        <CommandInput placeholder="Pick an input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {adjustedMinimumRequired.length > 0 && (
            <CommandGroup className="font-bold" heading="Required">
              {adjustedMinimumRequired
                .filter((x) => {
                  return x.backendName;
                })
                .map((x, index) => {
                  return renderModelInputMenuEntry(x, index);
                })}
            </CommandGroup>
          )}
          {adjustedOptional.length > 0 && (
            <CommandGroup className="font-bold" heading="Advanced">
              {adjustedOptional.map((x, index) => {
                return renderModelInputMenuEntry(x, index);
              })}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    );
  }

  function renderUsernameCreationDialogue() {
    if (usernameModalOpen == false) return;
    return (
      <BaseModalContainer
        title={`Create Username!`}
        modalContent={
          <CreateUsername
            loading={updateUsernameMutation.isPending}
            tryUsername={(username) => {
              toastPromise(
                updateUsernameMutation.mutateAsync({ username }),
                "Setting Username",
                `Hello @${username}!`,
                (error) => {
                  let finalErrorMessage = userErrorMessageForCode(
                    error?.[0]?.message
                  );
                  // TODO: add checks for PresetErrorCodes
                  return finalErrorMessage;
                }
              );
            }}
          />
        }
        onModalClose={() => {
          // setShowingInvoices(false)
        }}
        onModalDismiss={() => {
          setUsernameModalOpen(false);
          // setPresetDeletionInRequest(undefined);
          // setShowingPayoutHistory(false);
        }}
      />
    );
  }

  function handleSubmitPresetClicked() {
    if (taskData == undefined) return;
    if (selectedModelID == undefined) return;
    setLoading(true);
    const selectedTaskData = taskData.entries.find(
      (taskData) => taskData.id == selectedTaskID
    );

    const modelInfo = selectedTaskData?.modelsInfo.find(
      (model) => model.id == selectedModelID
    );
    if (selectedTaskData == undefined) return;
    if (modelInfo == undefined) return;

    BackendManager.createUserPresetForModel(
      selectedModelID,
      presetName,
      presetDescription,
      selectedTaskData.name,
      modelInfo.inputs,
      modelInfo.name,
      isPublic,
      isOpenSource,
      cost,
      entries,
      (progress) => {
        const adjustedProgress = Math.floor(progress * 100);
        setSubmissionProgress(adjustedProgress);
      }
    )
      .then((createdPreset) => {
        toastInfo("WOHOO, now share it with the world :) ");
        setLoading(false);
        setShowingShare(true);
        if (selectedTaskID) setCreatedTaskID(selectedTaskID);
        setCreatedModelID(selectedModelID);
        setCreatedPresetID(createdPreset.presetID);
      })
      .catch((error) => {
        switch (error?.[0].errorType) {
          case "Unauthorized":
            toastError("You dont have permission to take this action");
            break;
        }
        switch (error?.[0]?.message) {
          case PresetErrorCodes.NO_USERNAME:
            toastInfo(
              "Please create a username first",
              <div className="text-accent">
                <AiFillWarning />
              </div>
            );
            setConsentModalOpen(false);
            setUsernameModalOpen(true);
            break;
          case PresetErrorCodes.NAME_TAKEN:
            toastError(
              "Preset name already exists, please change your preset name"
            );
            setConsentModalOpen(false);
            break;
          case PresetErrorCodes.UNKNOWN:
          case undefined:
            console.log(error);
            toastError();
            break;
        }
        setLoading(false);
      });
  }

  function renderSubmissionDialogue() {
    if (consentModalOpen == false) return;
    return (
      <BaseModalContainer
        title={`Disclaimer!`}
        modalContent={
          <div className="inset-0 mx-auto mt-4 flex flex-row overflow-y-auto rounded-xl ">
            <form
              id="disclaimerform"
              method="dialog"
              className="mx-auto flex w-full flex-col space-y-4  font-bold"
            >
              <span className="rounded-md bg-primary p-1 text-primary-content">
                {`By proceeding to create the preset, you acknowledge that ${
                  isOpenSource
                    ? "it will be designated as Open Source"
                    : "it will be designated as Closed Source, ensuring that its underlying workings are not visible to any user"
                }`}
              </span>
              <span className="rounded-md bg-primary p-1 text-primary-content">
                {`${
                  isPublic
                    ? "it will be accessible and usable by any user on the MLTask platform."
                    : "it will ONLY be accessible and usable by yourself."
                }`}
              </span>
              <span className="rounded-md bg-primary p-1 text-primary-content">
                {`${
                  cost == 0
                    ? "this preset does not incur any additional cost for any user utilizing it."
                    : `each usage of this preset will incur an additional ${cost} credits to the user.`
                }`}
              </span>
              {cost != 0 && (
                <span className="rounded-md bg-primary p-1 text-primary-content">
                  {`MLTask shall retain a 30% commission from each instance of preset usage. You will keep 70% resulting in a equivalent amount of $${calculateTotalCreditAwardPerUsage(
                    cost
                  )} per usage`}
                </span>
              )}
              <span className="rounded-md bg-primary p-1 text-primary-content">
                MLTask retains the sole discretion to remove, delete, or modify
                any preset generated or submitted on the MLTask platform. This
                action may be taken with or without prior notice, and MLTask is
                not obligated to provide any justification for such actions.
              </span>
              <div className="form-control flex w-full flex-row space-x-2 font-bold">
                <input
                  type="checkbox"
                  checked={doesUserAgree}
                  onChange={(event) => {
                    setDoesUserAgree(event.target.checked);
                  }}
                  className="checkbox-accent checkbox my-auto"
                />
                <span className="label-text my-auto">
                  I understand, and I Agree.
                </span>
              </div>
              <ButtonWithLoading
                type="button"
                disabled={doesUserAgree == false}
                isLoading={loading}
                className={`btn btn-success mt-10 w-full`}
                onClick={() => {
                  handleSubmitPresetClicked();
                }}
              >
                {loading && (
                  <span className="text-secondary">{submissionProgress}%</span>
                )}
                Submit
              </ButtonWithLoading>
            </form>
          </div>
        }
        onModalClose={() => {
          // setShowingInvoices(false)
        }}
        onModalDismiss={() => {
          setConsentModalOpen(false);
          // setPresetDeletionInRequest(undefined);
          // setShowingPayoutHistory(false);
        }}
      />
    );
  }
  function renderCreatePresetButton() {
    return (
      <div>
        <button
          type="submit"
          disabled={
            entries.length == 0 || entries[0].presetEntryDatas?.length == 0
          }
          className={`btn btn-accent mt-10 w-full ${
            loading ? "btn-disabled" : ""
          }`}
        >
          {loading && <span className={`${loadingStyle} `}></span>}
          Create Preset
        </button>
      </div>
    );
  }
  function renderSharePortion() {
    if (showingShare == false) return;
    return (
      <SharePreset
        taskID={createdTaskID}
        modelID={createdModelID}
        presetID={createdPresetID}
        currentUser={currentUser}
      />
    );
  }
  function renderInputFields() {
    if (selectedTaskID == undefined) return;
    return (
      <div className="flex flex-col">
        {renderPresetNameAndDescription()}
        {renderInputsSection()}
        {renderAddInputToPresetSection()}
        {renderCreatePresetButton()}
      </div>
    );
  }
  function renderCreateForm() {
    return (
      <FormProvider {...methods}>
        <form
          id="creationForm"
          noValidate
          className="ng-untouched ng-pristine ng-valid my-auto flex h-full w-full flex-col space-y-8"
          onSubmit={handleSubmit((data) => {
            setConsentModalOpen(true);
          })}
        >
          <SimpleHeader headerText="Create Preset" />
          {renderTaskDrowDown()}
          {renderInputFields()}
        </form>
      </FormProvider>
    );
  }
  function renderMainPortion() {
    if (showingShare) return;
    return (
      <div className="flex h-full w-full p-4">
        {renderCreateForm()}
        {renderSubmissionDialogue()}
        {renderUsernameCreationDialogue()}
      </div>
    );
  }

  return (
    <>
      {getTaskError && <CreatePresetHero overrideBGColor="bg-base-200" />}
      {getTaskError == false && (
        <div className="flex h-full w-full">
          {renderSharePortion()}
          {renderMainPortion()}
        </div>
      )}
    </>
  );
}
